import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceIntelligenceV1Api,
  type FeaturePRD,
  type FeatureValidation,
  type PRDDashboard,
  type PRDBibleStatus,
  type ScopeItem,
} from '../../api/marketplace-intelligence-v1-prd';

type Tab = 'overview' | 'scope' | 'features' | 'detail' | 'status';
type Theme = 'light' | 'dark';

const categoryColors: Record<string, string> = {
  platform: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  intelligence: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  ops: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
};

const priorityColors: Record<string, string> = {
  p0: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  p1: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

export function MarketplaceIntelligenceV1PRDPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [selectedKey, setSelectedKey] = useState('authentication');
  const [dashboard, setDashboard] = useState<PRDDashboard | null>(null);
  const [scope, setScope] = useState<ScopeItem[]>([]);
  const [features, setFeatures] = useState<FeaturePRD[]>([]);
  const [detail, setDetail] = useState<FeaturePRD | null>(null);
  const [status, setStatus] = useState<PRDBibleStatus | null>(null);
  const [validation, setValidation] = useState<FeatureValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, sc, feat, st] = await Promise.all([
        marketplaceIntelligenceV1Api.dashboard(),
        marketplaceIntelligenceV1Api.scope(),
        marketplaceIntelligenceV1Api.features(),
        marketplaceIntelligenceV1Api.status(),
      ]);
      setDashboard(dash);
      setScope(sc);
      setFeatures(feat);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load PRD');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (tab !== 'detail') return;
    marketplaceIntelligenceV1Api.feature(selectedKey)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load feature PRD'));
  }, [tab, selectedKey]);

  const validateFeature = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceIntelligenceV1Api.validateFeature({
        feature_key: selectedKey,
        attrs: {
          business_objective: true, functional_requirements: true, ux_requirements: true,
          backend_requirements: true, database_defined: true, apis_defined: true,
          testing_defined: true, acceptance_criteria: true,
        },
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Feature PRD passes implementation readiness gate' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-emerald-900/40 bg-slate-900' : 'border-emerald-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-emerald-50/20 text-slate-900';
  const accent = dark ? 'text-emerald-300' : 'text-emerald-700';
  const tabActive = 'bg-emerald-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'scope', label: 'V1 Scope' },
    { id: 'features', label: 'Feature PRDs' },
    { id: 'detail', label: 'PRD Detail' },
    { id: 'status', label: 'Status' },
  ];

  const Section = ({ title, items }: { title: string; items?: string[] }) =>
    items && items.length > 0 ? (
      <div className="mb-4">
        <h4 className={`mb-1 text-sm font-semibold ${accent}`}>{title}</h4>
        <ul className={`list-inside list-disc text-sm ${muted}`}>
          {items.map((i) => <li key={i}>{i}</li>)}
        </ul>
      </div>
    ) : null;

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>PRD-000001</p>
          <h1 className="text-2xl font-semibold">Marketplace Intelligence V1</h1>
          <p className={`text-sm ${muted}`}>
            Core Platform Product Requirements — implementation blueprint with zero ambiguity
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-capability-architecture" className={`${accent} hover:underline`}>MCA-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/engineering-backlog-sprint-planning" className={`${accent} hover:underline`}>EBP-000001</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">V1 Core Platform Implementation Blueprint</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Every feature has business objectives, functional requirements, UX, backend, database, APIs, AI, testing, and acceptance criteria defined for immediate engineering implementation.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Scope Features', value: dashboard.scope_features },
              { label: 'Feature PRDs', value: dashboard.feature_prds },
              { label: 'Platform', value: dashboard.platform_features },
              { label: 'Intelligence', value: dashboard.intelligence_features },
              { label: 'Operations', value: dashboard.ops_features },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'scope' && (
        <div className="grid gap-3 md:grid-cols-2">
          {scope.map((s) => (
            <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold">{s.name}</h3>
                <div className="flex gap-1">
                  <span className={`rounded px-2 py-0.5 text-xs ${categoryColors[s.category] ?? ''}`}>{s.category}</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${priorityColors[s.priority] ?? ''}`}>{s.priority.toUpperCase()}</span>
                </div>
              </div>
              {s.description && <p className={`mt-2 text-sm ${muted}`}>{s.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'features' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Feature</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Priority</th>
                <th className="px-4 py-2">APIs</th>
                <th className="px-4 py-2">Route</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f) => (
                <tr
                  key={f.key}
                  className={`cursor-pointer border-b ${dark ? 'border-slate-800 hover:bg-slate-800' : 'border-slate-100 hover:bg-emerald-50'}`}
                  onClick={() => { setSelectedKey(f.key); setTab('detail'); }}
                >
                  <td className="px-4 py-2 font-medium">{f.name}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${categoryColors[f.category] ?? ''}`}>{f.category}</span></td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${priorityColors[f.priority] ?? ''}`}>{f.priority.toUpperCase()}</span></td>
                  <td className="px-4 py-2">{f.rest_endpoints?.length ?? 0}</td>
                  <td className={`px-4 py-2 font-mono text-xs ${muted}`}>{f.ui_route ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'detail' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {features.map((f) => (
              <button key={f.key} type="button" onClick={() => setSelectedKey(f.key)} className={`rounded-lg px-3 py-1.5 text-sm ${selectedKey === f.key ? tabActive : `${card} border`}`}>
                {f.name}
              </button>
            ))}
          </div>
          {detail && (
            <div className={`rounded-xl border p-5 ${card}`}>
              <h2 className="text-xl font-semibold">{detail.name}</h2>
              <p className={`mt-2 text-sm ${muted}`}><strong>Problem:</strong> {detail.problem_statement}</p>
              <p className={`mt-1 text-sm ${muted}`}><strong>Value:</strong> {detail.business_value}</p>
              <p className={`mt-1 text-sm ${muted}`}><strong>Persona:</strong> {detail.customer_persona}</p>
              {detail.kpis && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {detail.kpis.map((k) => <span key={k} className="rounded bg-emerald-100 px-2 py-0.5 text-xs dark:bg-emerald-900">{k}</span>)}
                </div>
              )}
              <div className="mt-6 grid gap-6 lg:grid-cols-2">
                <div>
                  <h3 className="mb-3 font-semibold">Functional & UX</h3>
                  <Section title="Behaviour" items={detail.detailed_behaviour} />
                  <Section title="Permissions" items={detail.permissions} />
                  <Section title="Loading States" items={detail.loading_states} />
                  <Section title="Error States" items={detail.error_states} />
                  <Section title="Accessibility" items={detail.accessibility} />
                </div>
                <div>
                  <h3 className="mb-3 font-semibold">Backend & Database</h3>
                  <p className={`mb-2 text-sm ${muted}`}>{detail.go_architecture}</p>
                  <Section title="Services" items={detail.services} />
                  <Section title="Tables" items={detail.tables} />
                  <Section title="REST Endpoints" items={detail.rest_endpoints} />
                  <Section title="Events" items={detail.events} />
                  {detail.dna_usage && <p className={`text-sm ${muted}`}><strong>DNA:</strong> {detail.dna_usage}</p>}
                </div>
                <div>
                  <h3 className="mb-3 font-semibold">Testing</h3>
                  <Section title="Unit" items={detail.unit_tests} />
                  <Section title="Integration" items={detail.integration_tests} />
                  <Section title="E2E" items={detail.e2e_tests} />
                  <Section title="Security" items={detail.security_tests} />
                </div>
                <div>
                  <h3 className="mb-3 font-semibold">Acceptance Criteria</h3>
                  <Section title="Definition of Done" items={detail.definition_of_done} />
                  <Section title="Production Readiness" items={detail.production_readiness} />
                  <Section title="Performance" items={detail.performance_criteria} />
                  <Section title="Security" items={detail.security_criteria} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{status.status}</span>
            {status.implementation_ready && <p className={`mt-3 text-sm ${muted}`}>Ready for immediate engineering implementation</p>}
            {status.no_ambiguity && <p className={`text-sm ${muted}`}>No implementation ambiguity remains</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Feature PRD</h3>
            <button type="button" disabled={busy} onClick={validateFeature} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Sample Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
